import { ChangeEvent, FormEvent, useEffect, useRef, useState, useCallback } from 'react';
import debounce from "lodash.debounce";
import Loader from '../../components/loader';
import { MdArrowUpward } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useCreateEpic } from '../../queries/epic';
import { useGetFactories, useGetMovieSubtitle, useIgnoreMovie, useSetMovieToFactory } from '../../queries/factory';
import { Movie, FactoryPagination, Subtitle } from '../../types/movie';
import { flags, flagsColor, flagsTranslate } from '../../constants';
import { Toast } from '../../components/toast';
import { CreateEpicRequest } from '../../types/epic';
import { Flag } from '../../types/tag';

export default function FactoryPage() {
  const [factories, setFactories] = useState<Movie[]>([]);
  const [subtitleItems, setSubtitleItems] = useState<Subtitle[]>([]);
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const [pagination, setPagination] = useState<FactoryPagination>();
  const searchRef = useRef<any>();
  const finalRequestData = useRef<Movie[]>([]);
  const selectedMovie = useRef<Movie>();
  const subtitleIdInputRef = useRef<any>(null);
  const subtitleIdSelectRef = useRef<any>(null);
  const { mutate: getFactoriesData, data: factoriesData, error, isLoading } = useGetFactories();
  const { mutate: createEpicRequest, data: createEpicData, error: createEpicError } = useCreateEpic();
  const { mutate: setMovieToFactory, data: movieToFactoryData, error: movieToFactoryError } = useSetMovieToFactory();
  const { mutate: getMovieSubtitle, data: movieSubtitleData } = useGetMovieSubtitle();
  const { mutate: ignoreMovieRequest, data: ignoreMovieData } = useIgnoreMovie();

  const handleLoadmore = () => {
    if (!pagination?.current) return;

    // reset search process
    searchRef.current.value = '';
    setFactories(factoriesData?.items || []);

    // request for next page
    getFactoriesData({ pageNumber: pagination.current + 1, searchValue: "" });
  };

  const toggleSubtitleModal = () => {
    if (subtitleIdInputRef.current?.value) {
      subtitleIdInputRef.current.value = '';
    }
    const modalHandler = document?.querySelector('#subtitleModal') as HTMLElement;
    modalHandler.click();

    return;
  };

  const handleTagClick = (movie: Movie, action: Flag) => {
    if (action === 'subtitle') {
      getMovieSubtitle({ movieId: movie.id });
      // show modal
      selectedMovie.current = movie;
      toggleSubtitleModal();
      return;
    }

    if (action === 'return') {
      const updatedList = factories.map((fact) => {
        if (fact.id === movie.id) {
          fact['flag'] = null;
          delete fact['subtitleId'];
        }
        return fact;
      });

      setFactories(updatedList);

      const filteredFinalRequestData = finalRequestData.current.filter((fact) => movie.id !== fact.id);
      finalRequestData.current = filteredFinalRequestData;
      return;
    }

    const finalList = factories.map((eachMovie) => {
      if (eachMovie.id === movie.id) {
        eachMovie['flag'] = [];
        eachMovie['flag'].push(action);
        finalRequestData.current.push(movie);
      }
      return eachMovie;
    });

    setFactories(finalList);

    return;
  };

  const handleSubmitMovieId = (e: FormEvent) => {
    e.preventDefault();
    const {
      movieId: { value: movieIdValue },
    } = e.target as HTMLFormElement;

    if (!movieIdValue) return;

    setMovieToFactory({
      movieId: movieIdValue,
    });
  };

  const handleSubmitSubId = (e: FormEvent) => {
    e.preventDefault();

    const {
      subId: { value: subIdValue },
    } = e.target as HTMLFormElement;

    if ((!subtitleIdSelectRef.current && !subIdValue) || isNaN(subIdValue)) {
      toast('لطفا آی‌دی زیرنویس را به درستی وارد کنید');
      return;
    }

    // update list
    const finalList = factoriesData?.items.map((movie) => {
      if (movie.id === selectedMovie.current?.id) {
        movie['flag'] = [];
        movie.flag.push('subtitle');
        movie.subtitleId = subIdValue ? subIdValue : subtitleIdSelectRef?.current?.value;
        finalRequestData.current.push(movie);
      }
      return movie;
    });

    finalList?.length && setFactories(finalList);

    // close modal
    toggleSubtitleModal();
  };

  const resetEverything = () => {
    finalRequestData.current = [];
    setFactories([]);
    getFactoriesData({ pageNumber: 1, searchValue: "" });
  };

  const handleSubmitList = () => {
    if (!finalRequestData?.current.length) return;

    finalRequestData.current.forEach((item) => {
      var tags = item.flag?.length ? item.flag?.map((flag) => [flagsTranslate[flag], flagsColor[flag]]) : []
      var factoryTags: string[][] = []
      item.tags.forEach((factoryTag) => {
        factoryTags.push([
          factoryTag, "#8D918D"
        ])
      })
      tags = tags.concat(factoryTags)

      const reqData: CreateEpicRequest = {
        movieId: item.id,
        duration: item.duration,
        uploader: item.uploader,
        title: item.title,
        tags: tags,
        language: item.language,
      };

      if (item.flag?.includes('subtitle')) {
        reqData.subId = item.subtitleId;
      }

      if (item.flag?.includes('ignore')) {
        ignoreMovieRequest({ movieId: item.id });
      } else {
        createEpicRequest(reqData);
      }
    });

    resetEverything();
    return;
  };

  const resetSelectedMovie = () => {
    selectedMovie.current = undefined;
    setSubtitleItems([]);
  };

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleKeyup = (e: KeyboardEvent) => {
    if (e.key.toLowerCase() === 'escape') {
      const confirmModal = document.querySelector('#subtitleModal') as HTMLInputElement;
      confirmModal.checked = false;
      resetSelectedMovie();
    }
  };

  useEffect(() => {
    if (factoriesData?.items?.length) {
      setFactories([...factories, ...factoriesData.items]);
      setPagination(factoriesData?.pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factoriesData]);

  useEffect(() => {
    getFactoriesData({ pageNumber: 1, searchValue: "" });

    const handleScrollButtonVisible = () => {
      window.scrollY > 300 ? setShowScrollButton(true) : setShowScrollButton(false);
    };

    window.addEventListener('scroll', handleScrollButtonVisible);
    window.addEventListener('keyup', handleKeyup);

    return () => {
      window.removeEventListener('scroll', handleScrollButtonVisible);
      window.removeEventListener('keyup', handleKeyup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { }, [createEpicData]);

  useEffect(() => { }, [createEpicError]);

  useEffect(() => {
    if (movieToFactoryData?.status === 200) {
      toast('فیلم با موفقیت به لیست اضافه شد.');
    }
  }, [movieToFactoryData]);

  useEffect(() => {
    if (movieSubtitleData?.subs?.length) {
      setSubtitleItems(movieSubtitleData?.subs);
    }
  }, [movieSubtitleData]);

  useEffect(() => {
    if (ignoreMovieData?.status === 200) {
      toast('عملیات با موفقیت انجام شد');
      setFactories([]);
      getFactoriesData({ pageNumber: 1, searchValue: "" });
    }
  }, [getFactoriesData, ignoreMovieData]);

  useEffect(() => {
    if (movieToFactoryError) {
      console.log(movieToFactoryError)
      if ((movieToFactoryError as { status: number })['status'] === 404) {
        toast('!پیدا نکردم', { type: 'error' });
      } else {
        toast((movieToFactoryError as { message: string }).message, { type: 'error' });
      }
      setFactories([]);
      getFactoriesData({ pageNumber: 1, searchValue: "" });
    }
  }, [getFactoriesData, movieToFactoryError]);

  const debouncedFilter = useCallback(debounce(searchTerm => {
    setFactories(factoriesData?.items || []);
    getFactoriesData({ pageNumber: 1, searchValue: searchTerm });
  }, 2000), [])

  const setSearchTerm = (query: string) => {
    debouncedFilter(query)
  }
  console.log(factoriesData)
  return (
    <div className="pt-5">
      <div className="flex sm:flex-row flex-col-reverse items-end my-5 justify-between">
        <input
          className="input w-full sm:max-w-sm input-bordered sm:mt-0 mt-5"
          placeholder="جستجو..."
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          ref={searchRef}
        />

        <div className="divider sm:none mt-10"></div>

        <div className="w-full sm:w-auto">
          <h3 className="title mb-2 font-bold">ورود به لیست فکتوری با Movie ID</h3>
          <form
            onSubmit={handleSubmitMovieId}
            className="flex"
          >
            <input
              type="tel"
              name="movieId"
              className="input input-bordered w-full max-w-xs ml-2"
              required
            />
            <button
              type="submit"
              className="btn btn-success"
            >
              ثبت
            </button>
          </form>
        </div>
      </div>

      <div className="flex mt-10">
        {/************************/}
        {/****** MAIN LIST *******/}
        {/************************/}
        <div className="flex flex-col w-1/3">
          <h3 className="title mb-2 font-bold">صف فکتوری فیلیمو {factoriesData && '(' + factoriesData?.pagination.total + ')'}</h3>
          <div className="divider"></div>
          <ul className="list-none">
            {factories?.map((factory: Movie, index) => {
              if (!factory.flag || factory.flag.length === 0) {
                return (
                  <li
                    key={`allFactories-${factory.id}-${index}`}
                    className="border-2 bg-white p-4 border-b-2 hover:bg-gray-200 mb-2"
                    data-id={factory.id}
                  >
                    <h3 className="item-title w-full text-right font-bold">{factory.title}</h3>

                    <div className="flex-flex-wrap">
                      {factory.tags?.map((tag) => (
                        <span
                          className="badge rounded-full badge-outline badge-sm ml-2"
                          key={tag}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <span className="flex flex-wrap py-3 mt-5">
                      {flags.map((flag) => (
                        <button
                          className={`btn ${flag.color} btn-sm btn-outline mb-2 ml-2 px-2`}
                          onClick={() => handleTagClick(factory, flag.slug as Flag)}
                          key={flag.slug}
                        >
                          {flag.name}
                        </button>
                      ))}
                    </span>
                  </li>
                );
              }
              return null;
            })}
          </ul>

          {pagination && pagination?.current < pagination?.lastPage && (
            <div className="flex justify-center mt-5">
              <button
                className="btn btn-outline"
                onClick={handleLoadmore}
              >
                بیشتر
              </button>
            </div>
          )}
        </div>

        {/*************************/}
        {/****** FINAL LIST *******/}
        {/*************************/}
        <div className="flex flex-col w-1/3 mx-2">
          <div className="flex items-center">
            <h3 className="title mb-2 font-bold">لیست نهایی</h3>
            <button
              className="btn btn-primary btn-sm mr-5"
              onClick={handleSubmitList}
            >
              ثبت نهایی
            </button>
          </div>
          <div className="divider"></div>
          <ul className="list-none">
            {factories?.map((factory: Movie) => {
              if (factory?.flag && !factory?.flag.includes('ignore')) {
                const hasForceTag = factory.flag.includes('force');
                const hasForce2Tag = factory.flag.includes('force2');
                const hasForce3Tag = factory.flag.includes('force3');
                const hasDoubleTage = factory.flag.includes('double');
                const hasArchiveTage = factory.flag.includes('archive');
                return (
                  <li
                    key={`finalList-${factory.id}`}
                    className="border-2 bg-white p-4 border-b-2 hover:bg-gray-200 mb-2"
                  >
                    <h3 className="item-title w-full text-right font-bold">{factory.title}</h3>

                    <div className="flex flex-wrap">
                      {factory.tags?.map((tag) => (
                        <span
                          className="badge badge-sm rounded-full badge-outline ml-2"
                          key={tag}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="divider"></div>

                    <div className="flex flex-wrap">
                      {factory.flag?.map((flag) => {
                        const flagColor = flags.filter((flg) => flg.slug === flag);
                        return (
                          <span
                            className={`badge ${flagColor[0].color} rounded-full ml-2`}
                            key={flag}
                          >
                            {flagsTranslate[flag]}
                          </span>
                        );
                      })}
                    </div>

                    <span className="flex flex-wrap py-3 mt-5">
                      <button
                        className={`btn btn-sm mb-2 ml-2 px-8`}
                        onClick={() => handleTagClick(factory, 'return')}
                      >
                        برگشت به لیست
                      </button>
                      {!hasForceTag && (
                        <button
                          className="btn btn-error btn-sm mb-2 ml-2 px-4"
                          onClick={() => {
                            factory.flag?.push('force');
                            setFactories([...factories]);
                          }}
                        >
                          فورس
                        </button>
                      )}
                      {!hasForce2Tag && (
                        <button
                          className="btn btn-error btn-sm mb-2 ml-2 px-4"
                          onClick={() => {
                            factory.flag?.push('force2');
                            setFactories([...factories]);
                          }}
                        >
                          فورس2
                        </button>
                      )}
                      {!hasForce3Tag && (
                        <button
                          className="btn btn-error btn-sm mb-2 ml-2 px-4"
                          onClick={() => {
                            factory.flag?.push('force3');
                            setFactories([...factories]);
                          }}
                        >
                          فورس3
                        </button>
                      )}
                      {!hasDoubleTage && (
                        <button
                          className="btn btn-accent btn-sm mb-2 ml-2 px-4"
                          onClick={() => {
                            factory.flag?.push('double');
                            setFactories([...factories]);
                          }}
                        >
                          دوبله
                        </button>
                      )}
                      {!hasArchiveTage && (
                        <button
                          className="btn btn-primary btn-sm mb-2 ml-2 px-4"
                          onClick={() => {
                            factory.flag?.push('archive');
                            setFactories([...factories]);
                          }}
                        >
                          اصلاحیه آرشیو
                        </button>
                      )}
                    </span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>

        {/**************************/}
        {/****** IGNORE LIST *******/}
        {/**************************/}
        <div className="flex flex-col w-1/3">
          <h3 className="title mb-2 font-bold">لیست رد شده ها</h3>
          <div className="divider"></div>
          <ul className="list-none">
            {factories?.map((factory: Movie) => {
              if (factory?.flag && factory?.flag.includes('ignore')) {
                return (
                  <li
                    key={`ignoreList-${factory.id}`}
                    className="border-2 bg-white p-4 border-b-2 hover:bg-gray-200 mb-2"
                  >
                    <h3 className="item-title w-full text-right font-bold">{factory.title}</h3>

                    <div className="flex-flex-wrap">
                      {factory.tags?.map((tag) => (
                        <span
                          className="badge badge-sm rounded-full badge-outline ml-2"
                          key={tag}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <span className="flex flex-wrap py-3 mt-5">
                      <button
                        className={`btn btn-sm mb-2 ml-2 px-8`}
                        onClick={() => handleTagClick(factory, 'return')}
                      >
                        برگشت به لیست
                      </button>
                    </span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>
      </div>

      <div className="flex w-full my-10 justify-center">
        <button
          className="btn btn-primary"
          // disabled={disableSubmitButton}
          onClick={handleSubmitList}
        >
          ثبت نهایی
        </button>
      </div>

      <input
        type="checkbox"
        id="subtitleModal"
        className="modal-toggle"
        onChange={(input) => {
          if (!input.target.checked) {
            resetSelectedMovie();
          }
        }}
      />
      <div className="modal">
        <div className="modal-box">
          <form onSubmit={handleSubmitSubId}>
            <p className="font-bold text-lg">ID زیرنویس را وارد کنید</p>
            {!!subtitleItems.length && (
              <select
                name="subSelect"
                className="select w-full max-w-xs input-bordered mt-5"
                ref={subtitleIdSelectRef}
              >
                {subtitleItems.map((sub) => (
                  <option
                    value={sub.id}
                    key={sub.id}
                  >
                    {sub.id} - {sub.lang}
                  </option>
                ))}
              </select>
            )}
            <input
              type="number"
              className="input w-full max-w-xs input-bordered mt-5"
              name="subId"
              placeholder="ID زیرنویس"
              ref={subtitleIdInputRef}
            />

            <div className="modal-action">
              <div className="flex w-full justify-between mt-10">
                <label
                  htmlFor="subtitleModal"
                  className="btn btn-outline btn-error"
                >
                  بستن
                </label>
                <button
                  className="btn btn-info px-10"
                  type="submit"
                >
                  بله
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <>
        {error && (
          <Toast
            message={JSON.stringify(error)}
            type="error"
            show={true}
          />
        )}
      </>

      {isLoading && <Loader />}

      {showScrollButton && (
        <button
          style={{ position: 'fixed', bottom: 20, left: 20 }}
          className="btn btn-outline btn-round"
          onClick={handleScrollTop}
        >
          <MdArrowUpward />
        </button>
      )}
    </div>
  );
}
